import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Sparkles } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const PROMO_MIN_QTY = 3;
const PROMO_RATE = 0.10;

export default function Cart() {
  const { items, updateQty, removeItem, total } = useCart();
  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const promoApplied = totalQty >= PROMO_MIN_QTY;
  const discountAmount = promoApplied ? +(total * PROMO_RATE).toFixed(2) : 0;
  const finalTotal = +(total - discountAmount).toFixed(2);
  const remaining = Math.max(0, PROMO_MIN_QTY - totalQty);
  const { user } = useAuth();
  const [form, setForm] = useState({
    shipping_name: "",
    shipping_address: "",
    shipping_city: "",
    shipping_postal: "",
  });
  const [loading, setLoading] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [addressLoaded, setAddressLoaded] = useState(false);

  useEffect(() => {
    if (!user || addressLoaded) return;
    api.get("/addresses").then((r) => {
      const defaultAddr = r.data.find((a) => a.is_default) || r.data[0];
      if (defaultAddr) {
        setForm({
          shipping_name: `${defaultAddr.first_name} ${defaultAddr.last_name}`.trim(),
          shipping_address: defaultAddr.address,
          shipping_postal: defaultAddr.postal,
          shipping_city: defaultAddr.city,
        });
      } else {
        setForm((prev) => ({ ...prev, shipping_name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.name || "" }));
      }
    }).catch(() => {
      setForm((prev) => ({ ...prev, shipping_name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.name || "" }));
    }).finally(() => setAddressLoaded(true));
  }, [user, addressLoaded]);

  const checkout = async (e) => {
    e.preventDefault();
    if (!cgvAccepted) return toast.error("Veuillez accepter les Conditions Générales de Vente");
    if (items.length === 0) return toast.error("Panier vide");
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        ...form,
      };
      const r = await api.post("/checkout/session", payload);
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du paiement");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="empty-cart">
        <ShoppingBag size={48} className="mx-auto text-terracotta mb-6" strokeWidth={1} />
        <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2">Oh non...</p>
        <h1 className="font-serif-display text-4xl mb-4">Votre panier est vide</h1>
        <p className="text-[#6B655C] mb-8">Découvrez nos pièces uniques faites main avec amour.</p>
        <Link to="/shop" className="btn-primary" data-testid="back-to-shop-btn">Découvrir la boutique</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="cart-page">
      <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2">Votre Panier</p>
      <h1 className="font-serif-display text-5xl mb-8">Récapitulatif</h1>

      {/* Promo banner */}
      <div className={`mb-10 rounded-3xl p-5 flex items-center gap-4 ${promoApplied ? "bg-patchwork text-white" : "bg-mustard/15 border border-mustard/30 text-[#1F1C18]"}`} data-testid="promo-banner">
        <Sparkles size={22} className={promoApplied ? "text-mustard" : "text-mustard"} />
        {promoApplied ? (
          <p><strong>Promo Trio activée ♡</strong> — Vous économisez {discountAmount.toFixed(2)} € (-10%)</p>
        ) : (
          <p>Plus que <strong>{remaining}</strong> article(s) à ajouter pour bénéficier de <strong>-10% (Promo Trio)</strong></p>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-5">
          {items.map((item) => (
            <div key={item.product_id} className="bg-white rounded-3xl border border-[#E5E1D8] p-5 flex gap-5" data-testid={`cart-item-${item.product_id}`}>
              <img src={item.image} alt={item.name} className="w-24 h-28 object-cover rounded-2xl bg-[#F7F5F0]" />
              <div className="flex-1">
                <h3 className="font-serif-display text-xl mb-1">{item.name}</h3>
                <p className="text-terracotta font-medium">{item.price.toFixed(2)} €</p>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-[#E5E1D8] rounded-full">
                    <button onClick={() => updateQty(item.product_id, item.quantity - 1)} className="p-2"><Minus size={14} /></button>
                    <span className="px-3 text-sm">{item.quantity}</span>
                    <button onClick={() => updateQty(item.product_id, item.quantity + 1)} className="p-2"><Plus size={14} /></button>
                  </div>
                  <button onClick={() => removeItem(item.product_id)} className="text-[#6B655C] hover:text-terracotta" data-testid={`remove-${item.product_id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className="font-medium text-lg">{(item.price * item.quantity).toFixed(2)} €</p>
            </div>
          ))}
        </div>

        <form onSubmit={checkout} className="bg-white rounded-3xl border border-[#E5E1D8] p-6 h-fit sticky top-28">
          <h2 className="font-serif-display text-2xl mb-5">Livraison</h2>
          <div className="space-y-3 mb-6">
            <input required value={form.shipping_name} onChange={(e) => setForm({ ...form, shipping_name: e.target.value })} placeholder="Nom complet" className="w-full p-3 rounded-2xl border border-[#E5E1D8] text-sm focus:outline-none focus:border-terracotta" data-testid="ship-name" />
            <input required value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} placeholder="Adresse" className="w-full p-3 rounded-2xl border border-[#E5E1D8] text-sm focus:outline-none focus:border-terracotta" data-testid="ship-address" />
            <div className="grid grid-cols-2 gap-3">
              <input required value={form.shipping_postal} onChange={(e) => setForm({ ...form, shipping_postal: e.target.value })} placeholder="Code postal" className="p-3 rounded-2xl border border-[#E5E1D8] text-sm focus:outline-none focus:border-terracotta" data-testid="ship-postal" />
              <input required value={form.shipping_city} onChange={(e) => setForm({ ...form, shipping_city: e.target.value })} placeholder="Ville" className="p-3 rounded-2xl border border-[#E5E1D8] text-sm focus:outline-none focus:border-terracotta" data-testid="ship-city" />
            </div>
          </div>
          <div className="border-t border-[#E5E1D8] pt-4 mb-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Sous-total</span><span data-testid="cart-subtotal">{total.toFixed(2)} €</span></div>
            {promoApplied && (
              <div className="flex justify-between text-moss" data-testid="cart-discount">
                <span>Promo Trio (-10%)</span><span>-{discountAmount.toFixed(2)} €</span>
              </div>
            )}
            <div className="flex justify-between text-[#6B655C]"><span>Livraison</span><span>Offerte ♡</span></div>
          </div>
          <p className="text-xs text-[#6B655C] leading-relaxed mb-4" data-testid="shipping-delay-notice">
            Pour la réception du colis en France métropolitaine par Colissimo, comptez entre 2 et 4 jours ouvrables et 8 à 20 jours ouvrables pour un département d'Outre-mer.
          </p>
          <div className="flex justify-between text-xl font-medium mb-6">
            <span>Total</span><span className="text-terracotta" data-testid="cart-total">{finalTotal.toFixed(2)} €</span>
          </div>
          <label className="flex items-start gap-3 mb-5 cursor-pointer select-none" data-testid="cgv-checkbox-label">
            <input
              type="checkbox"
              checked={cgvAccepted}
              onChange={(e) => setCgvAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-[#E5E1D8] accent-terracotta cursor-pointer shrink-0"
              data-testid="cgv-checkbox"
            />
            <span className="text-xs text-[#6B655C] leading-relaxed">
              J'ai lu et j'accepte les{" "}
              <Link to="/cgv" target="_blank" className="text-terracotta underline hover:text-terracotta/80" data-testid="cgv-link">
                Conditions Générales de Vente
              </Link>
            </span>
          </label>
          <button type="submit" disabled={loading || !cgvAccepted} className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-testid="checkout-stripe-btn">
            {loading ? "Redirection..." : "Payer avec Stripe"}
          </button>
          <p className="text-xs text-[#6B655C] text-center mt-3">Paiement sécurisé. Vos données sont protégées.</p>
        </form>
      </div>
    </div>
  );
}
